import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X, Plus } from "lucide-react";

const AI_MODELS = [
  { label: "GPT-5.4 (OpenAI)", provider: "openai", model: "gpt-5.4" },
  { label: "Claude Sonnet 4.6 (Anthropic)", provider: "anthropic", model: "claude-sonnet-4-6" },
  { label: "Gemini 3 Flash (Google)", provider: "gemini", model: "gemini-3-flash-preview" },
];

const Section = ({ title, desc, children, testid }) => (
  <div className="rounded-xl border border-[#334155] bg-[#1E293B] p-6" data-testid={testid}>
    <h3 className="font-heading font-medium">{title}</h3>
    {desc && <p className="text-sm text-slate-400 mt-1 mb-4">{desc}</p>}
    <div className={desc ? "" : "mt-4"}>{children}</div>
  </div>
);

const ListEditor = ({ label, items, onChange, testid }) => {
  const [val, setVal] = useState("");
  return (
    <div>
      <Label className="text-xs text-slate-400 mb-2 block">{label}</Label>
      <div className="flex flex-wrap gap-2 mb-3">
        {items.map((it) => (
          <span key={it} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#334155] bg-[#0F172A] text-sm">
            {it}
            <button onClick={() => onChange(items.filter((x) => x !== it))} className="text-slate-500 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && val.trim()) { onChange([...items, val.trim()]); setVal(""); } }} placeholder="Type and press Enter…" className="bg-[#0F172A] border-[#334155]" />
        <Button data-testid={`${testid}-add`} onClick={() => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } }} className="bg-[#334155] hover:bg-[#475569] text-slate-100 shrink-0"><Plus className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

export default function Settings() {
  const [s, setS] = useState(null);
  const [logs, setLogs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/change-log").then((r) => setLogs(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      toast.success("Settings saved");
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="py-32 text-center text-slate-500">Loading…</div>;

  const currentModel = AI_MODELS.find((m) => m.model === s.ai_model) || AI_MODELS[0];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Settings</h1>
          <p className="text-slate-400 text-sm mt-1">Manage categories, thresholds, lenders, the AI engine, and review the change log.</p>
        </div>
        <Button data-testid="settings-save" onClick={save} disabled={saving} className="bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] font-semibold">{saving ? "Saving…" : "Save Changes"}</Button>
      </div>

      <Tabs defaultValue="categories">
        <TabsList className="bg-[#1E293B] border border-[#334155]">
          <TabsTrigger value="categories" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120]">Categories</TabsTrigger>
          <TabsTrigger value="thresholds" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120]">Thresholds</TabsTrigger>
          <TabsTrigger value="ai" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120]">AI Engine</TabsTrigger>
          <TabsTrigger value="log" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120]">Change Log</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="mt-6 space-y-6">
          <Section title="Budget Category Management" testid="settings-categories">
            <ListEditor label="Expense Categories" items={s.expense_categories || []} onChange={(v) => set("expense_categories", v)} testid="cat" />
          </Section>
          <Section title="Department / Service Lines">
            <ListEditor label="Department / Service / Product Lines" items={s.department_lines || []} onChange={(v) => set("department_lines", v)} testid="dept-line" />
          </Section>
          <Section title="Centre Management" desc="Centres / branches used across Centre P&L and the dashboard Awish total.">
            <ListEditor label="Centres / Branches" items={s.centers || []} onChange={(v) => set("centers", v)} testid="centre" />
          </Section>
          <Section title="Income Classifications" desc="Income heads shown in Centre P&L for every centre.">
            <ListEditor label="Income Heads" items={s.income_heads || []} onChange={(v) => set("income_heads", v)} testid="income-head" />
          </Section>
          <Section title="Expenditure Heads" desc="Expense heads shown in Centre P&L for every centre.">
            <ListEditor label="Expense Heads" items={s.expense_heads || []} onChange={(v) => set("expense_heads", v)} testid="expense-head" />
          </Section>
          <Section title="Bank / Lender Management">
            <ListEditor label="Funding Sources" items={s.lenders || []} onChange={(v) => set("lenders", v)} testid="lender" />
          </Section>
        </TabsContent>

        <TabsContent value="thresholds" className="mt-6">
          <Section title="Threshold Configuration" desc="Control when warnings and alerts fire." testid="settings-thresholds">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div><Label className="text-xs text-slate-400 mb-1.5 block">Budget Warning Threshold (%)</Label><Input data-testid="threshold-budget" type="number" value={s.budget_warning_threshold} onChange={(e) => set("budget_warning_threshold", Number(e.target.value))} className="bg-[#0F172A] border-[#334155]" /></div>
              <div><Label className="text-xs text-slate-400 mb-1.5 block">Tax Reminder Lead Time (days)</Label><Input data-testid="threshold-tax" type="number" value={s.tax_reminder_lead_days} onChange={(e) => set("tax_reminder_lead_days", Number(e.target.value))} className="bg-[#0F172A] border-[#334155]" /></div>
              <div><Label className="text-xs text-slate-400 mb-1.5 block">Low Cash-Position Alert (₹)</Label><Input data-testid="threshold-cash" type="number" value={s.cash_low_balance_alert} onChange={(e) => set("cash_low_balance_alert", Number(e.target.value))} className="bg-[#0F172A] border-[#334155]" /></div>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="ai" className="mt-6">
          <Section title="AI Finance Intelligence Engine" desc="Choose the model that powers forecasting, insights, the query bar and the monthly summary." testid="settings-ai">
            <div className="flex items-center justify-between py-3 border-b border-[#334155] mb-4">
              <div><div className="font-medium text-sm">Enable AI Assistant</div><div className="text-xs text-slate-400">Turn the intelligence engine and summaries on or off.</div></div>
              <Switch data-testid="ai-enabled-switch" checked={!!s.ai_enabled} onCheckedChange={(v) => set("ai_enabled", v)} />
            </div>
            <Label className="text-xs text-slate-400 mb-1.5 block">AI Model</Label>
            <Select
              value={currentModel.label}
              onValueChange={(label) => { const m = AI_MODELS.find((x) => x.label === label); set("ai_provider", m.provider); set("ai_model", m.model); }}
            >
              <SelectTrigger data-testid="ai-model-select" className="bg-[#0F172A] border-[#334155] max-w-md"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-slate-100">
                {AI_MODELS.map((m) => <SelectItem key={m.label} value={m.label} className="focus:bg-[#334155]">{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Section>
        </TabsContent>

        <TabsContent value="log" className="mt-6">
          <Section title="Data Change Log" desc="Who changed what, and when — across all logged data." testid="settings-changelog">
            <div className="space-y-1 max-h-[480px] overflow-y-auto">
              {logs.length === 0 && <p className="text-sm text-slate-500">No changes recorded yet.</p>}
              {logs.map((l) => (
                <div key={l.id} className="flex items-center gap-3 text-sm py-2 border-b border-[#334155]/40">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${l.action === "delete" ? "bg-rose-600/15 text-rose-400" : l.action === "create" ? "bg-emerald-600/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>{l.action}</span>
                  <span className="text-slate-300">{l.entity}</span>
                  <span className="text-slate-500 flex-1 truncate">{l.details}</span>
                  <span className="text-slate-500 text-xs">{l.user_name}</span>
                  <span className="text-slate-600 text-xs">{new Date(l.timestamp).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
